import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import {
  useCreateUserPrivilegeMutation,
  useDeleteUserPrivilegeMutation,
} from "../../../Api/attoDeskApi";
import { IPrivilege } from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";
import useIsMobile from "../../../Core/hook/useIsMobile";
import {
  CRUD,
  Crud,
  CRUD_ACTIONS,
  humanizeModule,
  toModuleToken,
} from "../../UserRole/Components/permissionUtils";

export interface EditingModule {
  token: string; // module prefix, e.g. "Student"
  actions: Partial<Record<Crud, IPrivilege | null>>; // existing rows per action
}

interface Props {
  open: boolean;
  mode: "create" | "edit";
  module?: EditingModule | null;
  existingNames: Set<string>; // all current privilege names (lowercased)
  onClose: () => void;
  onSaved: () => void;
}

const PrivilegeModuleModal: React.FC<Props> = ({
  open,
  mode,
  module,
  existingNames,
  onClose,
  onSaved,
}) => {
  const isMobile = useIsMobile();
  const { showMessage, showErrorMessage } = useNotifier();
  const [createPrivilege] = useCreateUserPrivilegeMutation();
  const [deletePrivilege] = useDeleteUserPrivilegeMutation();
  const [busy, setBusy] = useState(false);

  const [moduleInput, setModuleInput] = useState("");
  const [checked, setChecked] = useState<Record<Crud, boolean>>({
    View: true,
    Create: false,
    Edit: false,
    Delete: false,
  });

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && module) {
      setModuleInput(humanizeModule(module.token));
      setChecked({
        View: !!module.actions.View,
        Create: !!module.actions.Create,
        Edit: !!module.actions.Edit,
        Delete: !!module.actions.Delete,
      });
    } else {
      setModuleInput("");
      setChecked({ View: true, Create: true, Edit: true, Delete: true });
    }
  }, [open, mode, module]);

  const token = useMemo(
    () => (mode === "edit" && module ? module.token : toModuleToken(moduleInput)),
    [mode, module, moduleInput]
  );

  const previewNames = CRUD.filter((a) => checked[a]).map(
    (a) => `${token || "Module"}_${a}`
  );

  const handleSave = async () => {
    if (!token) {
      showErrorMessage("Module name is required.");
      return;
    }
    const anyChecked = CRUD.some((a) => checked[a]);
    if (!anyChecked) {
      showErrorMessage("Select at least one action.");
      return;
    }
    setBusy(true);
    try {
      let created = 0;
      let removed = 0;
      for (const action of CRUD) {
        const name = `${token}_${action}`;
        const existingRow = module?.actions?.[action] || null;
        if (checked[action]) {
          // create if it doesn't already exist
          if (!existingRow && !existingNames.has(name.toLowerCase())) {
            const res: any = await createPrivilege({
              name,
              display_name: `${humanizeModule(token)} ${action}`,
            }).unwrap();
            if (res?.status === false) {
              showErrorMessage(res.message || `Failed to create ${name}`);
              setBusy(false);
              return;
            }
            created += 1;
          }
        } else if (existingRow?.id) {
          // unchecked but exists -> remove (edit mode only)
          const res: any = await deletePrivilege(
            String(existingRow.id)
          ).unwrap();
          if (res?.status === false) {
            showErrorMessage(res.message || `Failed to remove ${name}`);
            setBusy(false);
            return;
          }
          removed += 1;
        }
      }
      if (created === 0 && removed === 0) {
        showMessage("No changes to save.");
      } else {
        showMessage(
          mode === "create"
            ? `Module created (${created} permission${created === 1 ? "" : "s"}).`
            : `Module updated (${created} added, ${removed} removed).`
        );
      }
      onSaved();
      onClose();
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2, borderBottom: "1px solid #e2e8f0" }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
            {mode === "create" ? "Add Module" : "Edit Module"}
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Choose the actions available for this module
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent sx={{ p: 3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
          Module Name <span style={{ color: "#dc2626" }}>*</span>
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="e.g. Student, Class Schedule"
          value={moduleInput}
          onChange={(e) => setModuleInput(e.target.value)}
          disabled={mode === "edit"}
          helperText={
            mode === "edit"
              ? "Module name can't be changed here — toggle its actions below."
              : "Spaces are removed automatically (Class Schedule → ClassSchedule)."
          }
        />

        <Typography sx={{ fontSize: 13, fontWeight: 600, mt: 2, mb: 0.5 }}>
          Actions
        </Typography>
        <Stack>
          {CRUD_ACTIONS.map((a) => (
            <FormControlLabel
              key={a.key}
              control={
                <Checkbox
                  checked={checked[a.key]}
                  onChange={(e) =>
                    setChecked((p) => ({ ...p, [a.key]: e.target.checked }))
                  }
                  sx={{ color: a.color, "&.Mui-checked": { color: a.color } }}
                />
              }
              label={a.key}
            />
          ))}
        </Stack>

        {previewNames.length > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
            }}
          >
            <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.5 }}>
              Will create / keep:
            </Typography>
            <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "#334155" }}>
              {previewNames.join("  •  ")}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={busy}
          startIcon={
            busy ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveIcon />
          }
        >
          {mode === "create" ? "Create Module" : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrivilegeModuleModal;
